import classNames from 'classnames';
import Checkbox from 'components/base/Checkbox';
import FormControlWrapper from 'components/base/FormControlWraper';
import Radio from 'components/base/Radio';
import SVG from 'components/base/SVG';
import PropTypes from 'prop-types';
import React, { PureComponent, createRef } from 'react';
import { createPortal } from 'react-dom';
import isFullyVisible from 'utils/DOM/isFullyVisible';

import css from './style.scss';
import CheckboxItem from 'components/Checkbox';

let openDropdownInstance = null;

class GridCheckboxDropdown extends PureComponent {

  constructor(props) {
    super(props);

    this.ref = {};
    this.dropdownRef = createRef();
    this.portalRef = createRef();
    this.id = `GridCheckboxDropdown-${Math.random()}`;
    this.open = false;
    this.windowEventsAttached = false;

    this.handleRef = this.handleRef.bind(this);
    this.handleInputRef = this.handleInputRef.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.onClick = this.onClick.bind(this);
    this.propsOpen = this.propsOpen.bind(this);
    this.close = this.close.bind(this);
    this.onRef = this.onRef.bind(this);
    this.handleModeChange = this.handleModeChange.bind(this);
    this.updatePortalPosition = this.updatePortalPosition.bind(this);
    this.handleWindowEvent = this.handleWindowEvent.bind(this);

    // Set default mode to first mode if available
    const defaultMode = this.props.modes && this.props.modes.length > 0 
      ? this.props.modes[0].value 
      : 'ANY';
    
    this.state = { 
      searchName: '', 
      isOpen: false, 
      direction: 'bottom', 
      selectedValues: [], 
      mode: defaultMode,
      portalStyle: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: 'auto',
        zIndex: 9999,
      },
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);

    const { valueSep, modeSep, gridcontext, name, modes } = this.props;

    const filters = gridcontext.get('filters');
    const filterValue = filters.get(name) || null;
    
    if (filterValue) {
      const splitValues = filterValue.split(modeSep);
      const values = splitValues[1]?.split(valueSep).filter(v => v) || [];
      
      if (values.length > 0) {
        const modeValue = splitValues[0] || (modes && modes.length > 0 ? modes[0].value : 'ANY');
        this.setState({ 
          selectedValues: values, 
          mode: modeValue 
        });
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    this.detachWindowEvents();
    if (openDropdownInstance === this) {
      openDropdownInstance = null;
    }
  }

  attachWindowEvents() {
    if (this.windowEventsAttached) return;
    window.addEventListener('scroll', this.handleWindowEvent, true);
    window.addEventListener('resize', this.handleWindowEvent);
    this.windowEventsAttached = true;
  }

  detachWindowEvents() {
    if (!this.windowEventsAttached) return;
    window.removeEventListener('scroll', this.handleWindowEvent, true);
    window.removeEventListener('resize', this.handleWindowEvent);
    this.windowEventsAttached = false;
  }

  handleWindowEvent() {
    this.updatePortalPosition();
  }

  handleClickOutside(event) {
    const target = event.target;
    const containsRoot = this.dropdownRef.current && this.dropdownRef.current.contains(target);
    const containsPortal = this.portalRef.current && this.portalRef.current.contains(target);
    if (containsRoot || containsPortal) {
      return;
    }
    this.close();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen && !prevState.isOpen) {
      this.attachWindowEvents();
      this.updatePortalPosition();
    }

    if (!this.state.isOpen && prevState.isOpen) {
      this.detachWindowEvents();
    }

    if (this.state.isOpen) {
      if (this.element instanceof HTMLElement && this.state.direction === 'bottom') {
        requestAnimationFrame(() => {
          if (!isFullyVisible(this.element)) {
            //this.setState({ direction: 'top' });
          }
        });
      }
    }
    const { searchName } = this.ref;

    if (!searchName) return;

    if (!this.state.isOpen) this.open = false;
    else if (!this.open) {
      this.open = true;
      searchName.focus();
      searchName.select();
    }
  }

  getSelection() {
    const { value, valueSep, modeSep, options, gridcontext, name } = this.props;

    const { selectedValues } = this.state;
    const val = value || '';
    const modeIdx = val.indexOf(modeSep);
    const valIdx = modeIdx >= 0 ? modeIdx + 1 : 0;

    const filters = gridcontext.get('filters');
    const value1   = filters.get(name) || null;

    return {
      mode: val.substr(0, Math.max(modeIdx, 0)),
      values: selectedValues.length
        ? selectedValues.filter(v => options.some(o => String(o.value) === String(v)))
        : [],
    };
  }

  handleRef(el) {
    if (el) this.ref[el.name] = el;
  }

  handleInputRef() {
    const { name, onInputRef, onClick } = this.props;
    if (onInputRef) onInputRef({ name, focus: onClick });
  }

  handleModeChange(modeValue) {
    const { name, onChange, modeSep, valueSep } = this.props;
    
    this.setState({ mode: modeValue }, () => {
      // Build the filter value with mode and selected values
      const filterValue = `${modeValue}${modeSep}${this.state.selectedValues.join(valueSep)}`;
      
      onChange({
        name,
        value: filterValue,
      });
    });
  }

  updatePortalPosition() {
    if (!this.dropdownRef.current) return;

    const rect = this.dropdownRef.current.getBoundingClientRect();
    const portalStyle = {
      position: 'fixed',
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      overflow: 'visible',
    };

    this.setState({ portalStyle });
  }

  handleChange(ev) {
    const { name, options, onChange, modeSep, valueSep, modes } = this.props;
    const { name: field, value } = ev.target;

    // Handle search input
    if (field === 'searchName') {
      this.setState({ searchName: value });
      return;
    }
    
    // Handle mode radio buttons
    if (field.includes('-mode')) {
      const modeValue = value;
      this.setState({ mode: modeValue }, () => {
        // Build the filter value with mode and selected values
        const filterValue = `${modeValue}${modeSep}${this.state.selectedValues.join(valueSep)}`;
        
        onChange({
          name,
          value: filterValue,
        });
      });
      return;
    }

    // Handle checkbox items - use the value from the event
    const checkboxValue = String(value !== undefined ? value : field);
    
    // Toggle the value in the selectedValues array
    const currentSelected = this.state.selectedValues;
    const newSelectedValues = currentSelected.includes(checkboxValue)
      ? currentSelected.filter(v => v !== checkboxValue)
      : [...currentSelected, checkboxValue];

    this.setState({ selectedValues: newSelectedValues }, () => {
      // Call onChange with the updated selected values
      const filterValue = `${this.state.mode ? `${this.state.mode}${modeSep}` : ''}${newSelectedValues.join(valueSep)}`;
      
      onChange({
        name,
        value: filterValue,
      });
    });
  }

  onClick(event, ...rest) {
    event.stopPropagation();

    if (this.element && this.element.contains(event.target)) {
      return;
    }

    if (this.state.isOpen) {
      this.close();
    } else {
      if (openDropdownInstance && openDropdownInstance !== this) {
        // if (!this.isChildOf(openDropdownInstance)) {
        //   openDropdownInstance.close();
        // }
      }
      this.propsOpen();
    }

    return undefined;
  }

  propsOpen() {
    if (!this.state.isOpen) {
      openDropdownInstance = this;
      this.setState({ isOpen: true });
    }
  }

  close() {
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
    }
  }

  onRef(element) {
    this.element = element;
  }

  render() {
    const { size, placeholder, options, className, dropdownClassName, filterClassName, modes, searchable, loading } = this.props;
    const { searchName, isOpen, mode, portalStyle } = this.state;
    const { mode: selectedMode, values } = this.getSelection();
    const vals = !values.length || values.length === options.length ? [] : options.filter(o => values.includes(String(o.value)));
    const val = vals.map(o => o.label).join(' / ');
    const iconMode = !!(vals.length && vals[0].icon);

    const pinned = [];
    const unpinned = [];
    let group;
    const opts = options.filter(o => !searchable || searchName.trim() === '' || o.label.toLowerCase().includes(searchName.trim().toLowerCase()));
    opts.forEach((o) => {
      const name = o.group || '';
      if (!group || group.name !== name) {
        group = { name, options: [] };
        (o.pinned ? pinned : unpinned).push(group);
      }

      group.options.push(o);
    });

    const groups = [];
    if (pinned.length) groups.push({ name: 'pinned', options: pinned });
    if (unpinned.length) groups.push({ name: 'unpinned', options: unpinned });

    const portalDropdown = isOpen ? createPortal(
      <div ref={this.portalRef} style={portalStyle} className={css.portalDropdownRoot}>
        <div className={classNames(css.contactfilterDropdown, { [css.open]: true, [css.top]: this.state.direction === 'top' }, dropdownClassName)} ref={this.onRef}>
          {!searchable ? null : (
            <div className={css.search}>
              <FormControlWrapper>

                 <div className={css.inputWrapper}>
                <input type="text" className={css.inputWithIcon} value={searchName} name="searchName" onChange={this.handleChange} autoComplete="off" ref={this.handleRef} placeholder="Search..." />
                                      {/* <input
                                        type="text"
                                        onChange={this.handleFilterChange}
                                        value={searchString}
                                        className={css.inputWithIcon}
                                      /> */}
                                      <span className={css.iconWrapper}>
                                        <SVG icon="search-icon" />
                                      </span>
                                    </div>
              </FormControlWrapper>
            </div>
          )}
          {!modes || !modes.length ? null : (
            <div className={css.modes}>
              <div className={css.caption}>Match Mode</div>
              <div className={css.items}>
                {modes.map(m => {
                  const isActive = mode === m.value;
                  return (
                    <label 
                      key={m.value} 
                      className={`${css.radioLabel} ${isActive ? css.active : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleModeChange(m.value);
                      }}
                    >
                      <input
                        type="radio"
                        className={css.radioOption}
                        name={`${this.id}-mode`}
                        value={m.value}
                        checked={isActive}
                        onChange={this.handleChange}
                      />
                      {m.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {groups.map(({ name, options }) => (
            <div className={classNames(css.groupContainer, { [css.unpinned]: name === 'unpinned' })} key={name}>
              {options.map(({ name, options }) => (
                <div className={css.group} key={name}>
                  {!name || name === '' ? null : <div className={css.groupHeader}>{name}</div>}
                  {options.map(o => {
                    const isChecked = values.includes(String(o.value));
                    return (
                      <CheckboxItem 
                        onChange={this.handleChange} 
                        key={o.value} 
                        label={o.label} 
                        value={String(o.value)} 
                        icon={o.icon} 
                        color={o.color} 
                        checked={isChecked} 
                        containerClassName={css.checkboxStyle}
                        disabled={o.disabled || loading} 
                        labelClassName={css.checkboxLabelStyle}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
          {opts.length ? null : <div className={css.noResults}>No Matches Found</div>}
        </div>
      </div>,
      document.body
    ) : null;

    return (
      <div className={classNames(css.checkboxDropdown, className)} onClick={this.onClick} ref={this.dropdownRef}>
        <div className={classNames(css.filter, css[size], filterClassName)} title={val}>
          {iconMode ? null : <div className={`${css.value} placeholdertext`}>{!vals.length ? placeholder : val}</div>}
          {!iconMode ? null : (
            <div className={css.icons}>
              {vals.map(o => (
                <div key={o.icon}>
                  <SVG icon={o.icon} />
                  <div className={css.sep}>/</div>
                </div>
              ))}
            </div>
          )}
          <SVG icon="iconCaretDown" className={css.iconCaretDown} ref={this.handleInputRef} />
        </div>
        {portalDropdown}
      </div>
    );
  }
}

GridCheckboxDropdown.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  direction: PropTypes.string,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  filterClassName: PropTypes.string,
  searchable: PropTypes.bool,
  valueSep: PropTypes.string,
  modeSep: PropTypes.string,
  modes: PropTypes.array,
  skipCatcherCheck: PropTypes.bool,
};

GridCheckboxDropdown.defaultProps = {
  placeholder: 'All',
  direction: 'bottom',
  searchable: false,
  valueSep: ',',
  modeSep: '|',
  skipCatcherCheck: false,
};

export const AnyNoneAllModes = 'Any,None,All'.split(',').map(label => ({ label, value: label.toUpperCase() }));
export const AnyNoneModes = AnyNoneAllModes.slice(0, 2);

export default GridCheckboxDropdown;